/**
 * MongoDemoPanel — C054 MongoDB 문서 저장 라이브 데모
 * 계약: docs/common-specs/eraf-data-mongo/C054-mongodb.md §Δ.3 / §Δ.6
 * C055(KafkaDemoPanel)/C057(RabbitDemoPanel) 동형 패턴 — 단 왕복은 브로커 발행/수신이 아니라
 * document store 저장(save) → 목록 조회(list)다(실제 Mongo 영속 왕복 + 감사 6종 자동채움 시연).
 * 5상태: loading(상태조회 skeleton) / empty(저장 전 · 목록 0건) / error(상태조회 실패 · 저장 실패) /
 *        default(상태 + 저장결과 + 목록) / dialog(N/A — 인라인 콘솔, Kafka/RabbitMQ 동형)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMongoStatus, useMongoSave, useMongoList } from '../hooks/useMongoDemo';
import { mongoFormSchema, type MongoFormValues } from '../schema/showcaseSchema';
import type { ApiError } from '../types/showcase';

export function MongoDemoPanel() {
  const { t } = useTranslation();
  const statusQuery = useMongoStatus();
  const saveMutation = useMongoSave();
  const listQuery = useMongoList(20);

  const [saveError, setSaveError] = useState<ApiError | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<MongoFormValues>({
    resolver: zodResolver(mongoFormSchema),
    defaultValues: { message: '' },
  });

  // 저장 성공 후 자동 재조회 — save→list 왕복을 즉시 가시화
  useEffect(() => {
    if (!saveMutation.isSuccess) return;
    void listQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveMutation.data]);

  const status = statusQuery.data;
  const isStatusLoading = statusQuery.isLoading;
  const isStatusError = statusQuery.isError;
  const disabled = !status?.enabled;

  function onSubmit(values: MongoFormValues) {
    setSaveError(null);
    saveMutation.mutate(
      { message: values.message },
      {
        onSuccess: () => reset({ message: '' }),
        onError: (err) => setSaveError(err),
      }
    );
  }

  return (
    <section aria-labelledby="mongo-panel-title">
      <h2
        id="mongo-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.mongo.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.mongo.subtitle')}
      </p>

      {/* status — loading skeleton */}
      {isStatusLoading && (
        <div aria-busy="true" aria-label={t('showcase.mongo.status.loadingAriaLabel')} style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + ((i * 15) % 40)}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status — error */}
      {isStatusError && (
        <div role="alert" style={{ ...cardStyle, border: '1px solid var(--color-danger)' }}>
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {(statusQuery.error as ApiError)?.message ?? t('showcase.mongo.status.loadFailed')}
          </p>
          <button type="button" onClick={() => void statusQuery.refetch()} style={secondaryButtonStyle}>
            {t('showcase.common.retry')}
          </button>
        </div>
      )}

      {/* status — default */}
      {!isStatusLoading && !isStatusError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.mongo.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('showcase.mongo.status.enabled')}>
                <span style={{ color: status.enabled ? 'var(--color-status-success)' : 'var(--color-status-error)', fontWeight: 700 }}>
                  {status.enabled ? t('showcase.mongo.status.enabledYes') : t('showcase.mongo.status.enabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.mongo.status.host')}>
                <code style={monoStyle}>{status.host}</code>
              </InfoField>
              <InfoField label={t('showcase.mongo.status.database')}>
                <code style={monoStyle}>{status.database}</code>
              </InfoField>
              <InfoField label={t('showcase.mongo.status.collection')}>
                <code style={monoStyle}>{status.collection}</code>
              </InfoField>
              <InfoField label={t('showcase.mongo.status.docCount')}>
                <code style={monoStyle}>{status.docCount}</code>
              </InfoField>
            </div>
            {!status.enabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('showcase.mongo.disabledNotice')}
              </p>
            )}
          </div>

          {/* save */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.mongo.save.title')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  id="mongo-message"
                  type="text"
                  placeholder={t('showcase.mongo.save.placeholder')}
                  aria-label={t('showcase.mongo.save.title')}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'mongo-message-error' : undefined}
                  disabled={disabled}
                  {...register('message')}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-3)',
                    border: `1px solid ${errors.message ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontFamily: 'var(--font-family-sans)',
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
                    color: 'var(--color-text-primary)',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.message && (
                  <p id="mongo-message-error" role="alert" style={errorTextStyle}>
                    {errors.message.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={disabled || !isValid || saveMutation.isPending}
                aria-busy={saveMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  opacity: disabled || saveMutation.isPending ? 0.6 : 1,
                  cursor: disabled || saveMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {saveMutation.isPending ? t('showcase.mongo.save.sending') : t('showcase.mongo.save.button')}
              </button>
            </form>

            {/* save — error */}
            {saveError && !saveMutation.isPending && (
              <div role="alert" style={{ marginTop: 'var(--spacing-3)', ...errorBoxStyle }}>
                {saveError.message ?? t('showcase.mongo.save.error')}
              </div>
            )}

            {/* save — empty (아직 저장 전) */}
            {!saveMutation.data && !saveError && !saveMutation.isPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.mongo.save.empty')}
              </p>
            )}

            {/* save — default (결과) */}
            {saveMutation.data && !saveMutation.isPending && (
              <div
                role="region"
                aria-label={t('showcase.mongo.save.resultLabel')}
                style={{ marginTop: 'var(--spacing-4)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)' }}
              >
                <InfoField label={t('showcase.mongo.save.result.id')}>
                  <code style={monoStyle}>{saveMutation.data.id}</code>
                </InfoField>
                <InfoField label={t('showcase.mongo.save.result.collection')}>
                  <code style={monoStyle}>{saveMutation.data.collection}</code>
                </InfoField>
                <InfoField label={t('showcase.mongo.save.result.createdBy')}>
                  <code style={monoStyle}>{saveMutation.data.createdBy}</code>
                </InfoField>
                <InfoField label={t('showcase.mongo.save.result.createdAt')}>
                  <code style={monoStyle}>{saveMutation.data.createdAt}</code>
                </InfoField>
              </div>
            )}
          </div>

          {/* list */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)' }}>
              <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>{t('showcase.mongo.list.title')}</h3>
              <button
                type="button"
                onClick={() => void listQuery.refetch()}
                disabled={listQuery.isFetching}
                style={secondaryButtonStyle}
              >
                {listQuery.isFetching ? t('showcase.mongo.list.loading') : t('showcase.mongo.list.refreshButton')}
              </button>
            </div>

            {listQuery.isFetching && (
              <p aria-live="polite" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('showcase.mongo.list.loading')}
              </p>
            )}

            {listQuery.isError && !listQuery.isFetching && (
              <div role="alert" style={errorBoxStyle}>
                {(listQuery.error as ApiError)?.message ?? t('showcase.mongo.list.error')}
              </div>
            )}

            {!listQuery.isFetching && !listQuery.isError && (!listQuery.data || listQuery.data.items.length === 0) && (
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.mongo.list.empty')}
              </p>
            )}

            {!listQuery.isFetching && listQuery.data && listQuery.data.items.length > 0 && (
              <div role="table" aria-label={t('showcase.mongo.list.tableLabel')} style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)' }}>
                  <thead>
                    <tr>
                      {[
                        t('showcase.mongo.list.col.message'),
                        t('showcase.mongo.list.col.createdBy'),
                        t('showcase.mongo.list.col.createdAt'),
                      ].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          style={{
                            padding: 'var(--spacing-2)',
                            textAlign: 'left',
                            borderBottom: '1px solid var(--color-surface-border)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listQuery.data.items.map((item) => (
                      <tr key={item.id}>
                        <td style={tdStyle}>{item.message}</td>
                        <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)' }}>{item.createdBy}</td>
                        <td style={tdStyle}>{item.createdAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.mongo.learning')}
      </div>
    </section>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const errorTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  color: 'var(--color-danger)',
  fontSize: 'var(--font-size-sm)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-primary)',
};
